<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="warehouse"
                            variant="page"
                            :title="$t('Stock items')"
                            :description="$t('billing.features.warehouse.tooltip')"
                            icon="ti-package">
          <Toolbar class="p-mb-4">
            <template #start >
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">
                {{ $t('Stock items') }}
              </div>
              <AddNewButton v-show="canModerate" :label="$t('Add')" @click="addNewItem" class="p-mr-2"/>
              <Button class="p-button-secondary p-button-raised p-mr-2"
                      @click="changeItemsCategoryOnClick"
                      icon="pi pi-file-edit"
                      v-show="canModerate"
                      :disabled="!selectedItems?.length || dataTableIsLoading || billingDataEditingLocked"
                      :label="selectedItems?.length
                        ? `${$t('changeCategoryLabel')} (${selectedItems.length})`
                        : $t('changeCategoryLabel')"/>
            </template>
            <template #end>
              <div class="p-d-flex p-flex-wrap">
                <!--              //DONT DELETE!!!!-->
                <!--              <div class="p-d-flex p-ai-center p-mr-2" v-if="canModerate">-->
                <!--                <Checkbox :disabled="dataTableIsLoading"-->
                <!--                          @change="toggleShowNotActive"-->
                <!--                          id="stock-items-show-not-active-inputswitch"-->
                <!--                          :binary="true"-->
                <!--                          v-model="showNotActive"/>-->
                <!--                <div>-->
                <!--                  <label for="stock-items-show-not-active-inputswitch" >-->
                <!--                    <div class="p-p-1" :class="{'pointer': !dataTableIsLoading}">Show not active</div>-->
                <!--                  </label>-->
                <!--                </div>-->
                <!--              </div>-->
                <CustomDropdown id="warehouse-item-category-id"
                                class="p-mr-2"
                                ref="selectedCategoryDropdown"
                                style="min-width: 180px; max-width: 200px;"
                                data-key="id"
                                v-model="selectedCategory"
                                :options="categoriesList"
                                :disabled="dataTableIsLoading"
                                :filterPlaceholder="$t('findCategoryPlaceholder')"
                                optionLabel="name"
                                :filter="true"
                                :filterFields="['name']"
                                @change="changeCategory"
                                :showClear="true">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <div>{{ slotProps.value.name }}<span v-if="slotProps.value.count"> ({{slotProps.value.count}})</span></div>
                    </div>
                    <span v-else>{{ $t('selectCategoryPlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div v-if="slotProps.option">
                      <div :class="{'bold-text' : slotProps.option.id === 0}">{{ slotProps.option.name }}<span v-if="slotProps.option.count || slotProps.option.count === 0"> ({{slotProps.option.count}})</span></div>
                    </div>
                  </template>
                </CustomDropdown>
                <SearchInput :disabled="dataTableIsLoading" @change-processed-search-value="changeProcessedSearchValue" v-model="searchData" @show-spinner="isLoading = true"/>
              </div>
            </template>
          </Toolbar>

          <Toolbar class="p-mb-4 p-pt-3 p-pb-3" v-show="minQtyItemsCount && +minQtyItemsCount">
            <template #start>
              <div class="p-d-flex p-ai-center ">
                <i class="pi pi-exclamation-triangle p-mr-1"></i>
                <span class="p-mr-1"><b>{{ $t('Notice') }}: </b>{{ $t('lowStockQtyItemsAlert') }}</span>
                <span class="p-badge p-badge-warning badge">{{ minQtyItemsCount }}</span>
              </div>
              <div>

              </div>
            </template>
            <template #end>
              <div class="p-d-flex p-ai-center p-mr-2">
                <Checkbox :disabled="dataTableIsLoading"
                          @change="toggleShowMinQtyItems"
                          id="stock-items-show-min-qty-items-inputswitch"
                          :binary="true"
                          v-model="showMinQtyItems"/>
                <div>
                  <label for="stock-items-show-min-qty-items-inputswitch" >
                    <div class="p-p-1" :class="{'pointer': !dataTableIsLoading}">{{ $t('showLowStockItemsLabel') }}</div>
                  </label>
                </div>
              </div>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :dataTableIsLoading="dataTableIsLoading"
                            :showHeader="false"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :tableClass="'warehouse-items'"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                            {name: 'code', title: $t('Code'), class: 'col-code', sortable: true},
                            {name: 'name', title: $t('Name'), class: 'col-name', sortable: true},
                            {name: 'warehouse_category_id', title: $t('Category'), class: 'no-print', sortable: false},
                            {name: 'unit', title: $t('unitLabel'), class: 'col-unit', sortable: false},
                            {name: 'qty', title: $t('Qty'), minWidth: '70px', sortable: false},
                            {name: 'min_qty', title: $t('minQtyLabel'), minWidth: '70px', class: 'no-print', sortable: false},
                            {name: 'reserved_qty', title: $t('reservedShortLabel'), sortable: false},
                            {name: 'available', title: $t('lineItemAvailableQtyLabel'), class: 'no-print', sortable: false},
                            {name: 'purchase_price', title: $t('purchasePriceShortLabel'), minWidth: '70px', sortable: false},
                            {name: 'sell_price', title: $t('sellingPriceShortLabel'), minWidth: '70px', sortable: false},
                            {name: 'markup', title: $t('markupPercentLabel'), minWidth: '70px', isHidden: !canModerate, class: 'no-print',  sortable: false},
                            {name: 'status', title: '', sortable: !!showNotActive, isHidden: !showNotActive || showNotActiveChanged, class: 'no-print' },
                            {name: 'created_at', title: $t('Created'), sortable: true, isHidden: !canModerate, class: 'no-print' },
                          ]">
            <template v-slot:multiselectHeader>
              <th v-show="canModerate" class="multiSelect-column-header no-print">
                <Checkbox @click="selectAllCheckboxOnClick"
                          :class="{ 'billing-selection-checkbox-disabled': billingDataEditingLocked }"
                          :disabled="dataTableIsLoading || billingDataEditingLocked"
                          :value="0"
                          v-model="selectAllCheckbox"/>
              </th>
            </template>
            <template v-slot:body>
              <tr v-for="item of items"
                  :key="item.id"
                  :class="{'p-highlight': isItemSelected(item.id), 'updated-item': isItemUpdated(item.id)}">
                <td v-show="canModerate" class="no-print">
                  <Checkbox :value="item.id"
                            :class="{ 'billing-selection-checkbox-disabled': billingDataEditingLocked }"
                            :disabled="dataTableIsLoading || billingDataEditingLocked"
                            v-model="selectedItems"/>
                </td>
                <td class="col-code">
                  <span v-if="item.code">{{ item.code }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="col-name">
                  <span v-if="item.name" class="word-break">{{ item.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="no-print">
                  <span v-if="item.warehouse_category_id">{{ categoriesList?.find(c => c.id === item.warehouse_category_id)?.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  {{ unitLabel(item.unit) }}
                </td>

                <td :class="{'orange-soft-background' : !isItemSelected(item.id) && isLowStockItem(item) }">
                  <span :class="{'orange-color' : isLowStockItem(item) }">{{ formatDecimal(item.qty, true, item.is_decimal ? 2 : 0) }}</span>
                </td>
                <!--              <td :class="{'orange-color orange-background' : item.min_qty && item.qty < item.min_qty }">-->
                <td class="no-print" :class="{'orange-soft-background': !isItemSelected(item.id) && isLowStockItem(item)}">
                <span v-if="item.min_qty">
                  <span>{{ formatDecimal(item.min_qty, true, item.is_decimal ? 2 : 0) }}</span>
                </span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <div v-if="item.reserved_qty && +item.reserved_qty !== 0">
                    <a @click.prevent="getReservedOrderPositions($event, item)"
                       draggable="false"
                       href="#">
                      {{ formatDecimal(item.reserved_qty) }}
                    </a>
                    <OverlayPanel :ref="'wirq-' + item.id" :autoZIndex="$store.state.firstLayerIsOpened">
                      <Spinner v-if="item.isLoading" :isSmall="true" :spinnerStyle="'height:60px'"/>
                      <div v-else class="overlay-panel-inner">
                        <div v-if="item.reservedOrderPositions?.length" style="line-height: 1.4">
                          <div class="title">{{ $t('Orders') }}:</div>
                          <div v-for="(orderPosition, index) of item.reservedOrderPositions" :key="orderPosition.id">
                            <div v-if="orderPosition.uid && orderPosition.order_number">
                              <router-link
                                  :to="{ path: `/orders/${orderPosition.uid}` }"
                                  :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                                  rel="noopener">
                                {{ orderPosition.order_number }}
                              </router-link>
                              <div>{{$t('Qty')}}: {{ formatDecimal(orderPosition.qty) }}</div>
                            </div>
                            <hr v-show="index < item.reservedOrderPositions.length - 1">
                          </div>
                        </div>
                        <div v-else>{{ $t('No data available') }}</div>
                      </div>
                    </OverlayPanel>
                  </div>
                  <i v-else class="ti ti-minus"/>
                </td>
                <td class="no-print" >
                  {{ formatDecimal(item.qty - item.reserved_qty) }}
                  <!--                {{ formatDecimal(item.qty - item.reserved_qty, true, item.is_decimal ? 2 : 0) }}-->
                </td>
                <td :class="{'red-soft-background' : !isItemSelected(item.id) && checkSellingPriceIsTooLow(item)}">
                  <span>{{ formatCurrency(item.purchase_price, 2, 4) }}</span>
                </td>
                <td :class="{'red-soft-background' : !isItemSelected(item.id) && checkSellingPriceIsTooLow(item)}">
                  <span :class="{'warning-color' : checkSellingPriceIsTooLow(item)}">{{ formatCurrency(item.sell_price, 2, 4) }}</span>
                </td>
                <td v-if="canModerate" class="no-print">
                  <span>{{ calculateItemMarkup(item) }}</span>
                </td>
                <td class="no-print" v-if="showNotActive && !showNotActiveChanged">
                  <WarehouseItemStatusButton :item="item" @update-items="updateItems"/>
                </td>
                <td class="no-print table-date-line" v-show="canModerate">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.createdBy) }}
                </span>
                  <i v-else class="ti-minus"/>
                </td>
                <td v-show="actionButtonsCount" class="no-print">
                  <div class="actions">
                    <EditButton :disabled="dataTableIsLoading"
                                @click="editItem(item)" />
                    <DeleteButton @click="confirmItemDelete(item)"
                                  :disabled="dataTableIsLoading"/>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="!itemsArrayIsEmpty"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <WarehouseItemModal :visible="itemModal"
                              :item="item"
                              :selectedCategoryId="selectedCategoryId"
                              @update-items="updateItems"
                              @close="closeItemModal"/>

          <WarehouseCategoryModal :visible="categoryModal"
                                  :item="category"
                                  @update-items="updateWarehouseCategory"
                                  @close="closeCategoryModal"/>

          <ChangeCategoryModal :visible="changeCategoryModal"
                               :warehouseItemsCategoryModal="true"
                               :categories="categories"
                               :selectedItems="selectedItems"
                               @change-items-category="changeItemsCategory"
                               @close="closeChangeCategoryModal"/>

          <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                              :modalDataIsLoading="modalDataIsLoading"
                              @close="closeConfirmItemDeleteModal"
                              @delete-item="deleteItem"
                              :data="itemToDeleteData"/>
        </BillingFeatureGate>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import WarehouseCategoryModal from "@/pages/data_directory/components/WarehouseCategoryModal";
import WarehouseItemModal from "@/pages/warehouse/components/WarehouseItemModal";
import formatMixins from "@/mixins/formatMixins";
import datatableMixins from "@/mixins/datatableMixins";
import httpMixins from "@/mixins/httpMixins";
import units from "@/translations/units";
import ChangeCategoryModal from "@/components/ChangeCategoryModal.vue";
import WarehouseItemStatusButton from "@/pages/warehouse/components/WarehouseItemStatusButton";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import BillingFeatureGate from "@/components/billing/BillingFeatureGate.vue";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'

export default {
  mixins: [ formatMixins, datatableMixins, httpMixins, showErrorsMixins, billingFeaturesMixins, billingRestrictionsMixin ],
  components: { WarehouseItemModal, WarehouseCategoryModal, ChangeCategoryModal, WarehouseItemStatusButton, BillingFeatureGate },
  data() {
    return {
      showNotActiveChanged: false,
      getCategoriesFetchInFlight: false,
      showMinQtyItems: false,
      minQtyItemsCount: 0,
      selectedCategory: null,
      showNotActive: false,
      expandedKeys: {},
      keysAreExpanded: false,
      categoriesList: [],
      categories: [],
      fieldsExpand: 'createdBy',
      apiLink: 'warehouse/get-warehouse-items',
      deleteItemUrl: 'warehouse/delete-item',
      routePath: '/stock-items',
      sort: {
        column: 'code',
        direction: '',
      },
      totalItemsCount: null,
      categoryModal: false,
      changeCategoryModal: false,
      category: {},
      units,
      settings
    }
  },
  watch: {
    '$i18n.locale'() {
      this.translateUncategorizedCategory()
    },
    '$store.state.firstLayerIsOpened'() {
      if (!this.$store.state.firstLayerIsOpened) {
        if (this.categoryModal) {
          this.closeCategoryModal()
        }
        if (this.changeCategoryModal) {
          this.closeChangeCategoryModal()
        }
      }
    },
    canUseWarehouse(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetWarehouseState()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    if (this.canUseWarehouse) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    getPageData(showSpinner = false) {
      if (!this.canUseWarehouse || !this.userCanGetData) return
      this.getCategories().then(() => this.getItems(showSpinner))
    },
    resetWarehouseState() {
      this._abortors?.warehouseItems?.abort?.()
      this.items = []
      this.categories = []
      this.categoriesList = []
      this.selectedCategory = null
      this.selectedItems = []
      this.selectAllCheckbox = []
      this.minQtyItemsCount = 0
      this.isLoading = false
      this.dataTableIsLoading = false

      this.itemModal = false
      this.categoryModal = false
      this.changeCategoryModal = false
      this.confirmItemDeleteModal = false
    },
    toggleShowMinQtyItems() {
      this.showNotActive = false
      this.selectedCategory = null
      this.changeCategory()
      this.getPageData(true)
    },
    isLowStockItem(item) {
      const qty = Number(item?.qty || 0)
      const minQty = Number(item?.min_qty || 0)

      return minQty > 0 && qty < minQty
    },
    unitLabel(unitNum) {
      const unit = this.units.find(unit => Number(unit.num) === Number(unitNum))

      return unit?.label?.[this.$i18n.locale] || unit?.label?.en || ''
    },
    translateUncategorizedCategory() {
      const uncategorizedCategory = this.categoriesList?.find(c => c.id === 0)
      if (uncategorizedCategory) {
        uncategorizedCategory.name = this.$t('Uncategorized')
      }
    },
    checkSellingPriceIsTooLow(item) {
      const sellPrice = item.sell_price ? +item.sell_price : 0
      const purchasePrice = item.purchase_price ? +item.purchase_price : 0
      return sellPrice < purchasePrice
    },
    async getReservedOrderPositions($event, warehouseItem) {
      if (!warehouseItem?.id) return
      let warehouseItemId = warehouseItem.id
      this.toggleReservedOrderPositionsOverlay($event, warehouseItem.id)

      if (!this.$refs['wirq-' + warehouseItem.id.toString()]?.visible) return false

      warehouseItem.isLoading = true
      const apiLink = 'warehouse/get-reserved-order-positions'
      try {
        const { data, status } = await httpClient.post(apiLink, { warehouseItemId })
        if (status === 200 && data) {
          warehouseItem.reservedOrderPositions = data.reserved_positions ?? []
          warehouseItem.reserved_qty = data.reserved_qty ??  0
        }
      } catch(err) {
        if (!this.canUseWarehouse) return
        this.showError(err)
      } finally {
        warehouseItem.isLoading = false
      }
    },
    toggleReservedOrderPositionsOverlay(event, id) {
      if (!event || !id) {
        return false
      }
      this.$refs['wirq-' + id.toString()]?.toggle(event);
    },
    updateWarehouseCategory() {
      this.getPageData()
    },
    toggleShowNotActive() {
      this.showNotActiveChanged = true
      this.selectedItems = []
      this.selectAllCheckbox = []
      if (this.showNotActive) {
        this.showMinQtyItems = false
      }
      // try {
      this.getPageData(true)
      // } finally {
      //   this.showNotActiveChanged = false
      // }
      // this.getCategories().then(() => this.getItems(true))
    },
    async actualizeSorting() {
      try {
        const { status } = await httpClient('warehouse/set-items-sorting')
        if (status === 200) {
          this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: settings.toastLife})
        }
      } catch (err) {
        this.showError(err)
      }
    },
    changeItemsCategory() {
      this.updatedItemId = null
      this.updatedItemsIds = [ ...this.selectedItems ]
      this.selectedItems = []
      this.getPageData()
      this.closeChangeCategoryModal()
    },
    changeItemsCategoryOnClick() {
      if (this.billingActionIsRestricted()) return false

      this.changeCategoryModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeChangeCategoryModal() {
      this.changeCategoryModal = false
      this.$store.commit('toggleFirstLayer', false)
      this.submitted = false
    },
    // async actualizeReservedQty() {
    //   try {
    //     const { status, data } = await httpClient('warehouse/actualize-reserved-qty')
    //     if (status === 200 && data?.success) {
    //       this.getItems(true)
    //       this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: settings.toastLife})
    //     }
    //   } catch(err) {
    //     this.showError(err)
    //   }
    // },
    // calculateItemMarkup(item) {
    //   if (!item) return false
    //   const margin = ((item.sell_price * 100) / item.purchase_price) - 100
    //   if (+margin && isFinite(+margin)) {
    //     return this.formatDecimal(margin, true, 2) + '%'
    //   } else {
    //     return
    //   }
    // },
    calculateItemMarkup(item) {
      const sellPrice = Number(item?.sell_price || 0)
      const purchasePrice = Number(item?.purchase_price || 0)

      if (!sellPrice || !purchasePrice) return ''

      const markup = ((sellPrice * 100) / purchasePrice) - 100

      return Number.isFinite(markup)
          ? `${this.formatDecimal(markup, true, 2)}%`
          : ''
    },
    changeCategory() {
      this.selectedCategoryId = this.selectedCategory?.id

      if (!this.selectedCategory) this.$refs.selectedCategoryDropdown?.clearFilterValue()

      const route = {
        route: this.$route.path,
        query: {
          ...this.$route.query,
          category: this.selectedCategoryId,
        }
      }

      if (!this.selectedCategoryId && this.selectedCategoryId !== 0) {
        delete route.query.category
      }
      this.selectedItems = []
      this.$router.push(route)
    },
    //
    async getCategories() {
      if (!this.canUseWarehouse || !this.userCanGetData || this.getCategoriesFetchInFlight) return
      this.getCategoriesFetchInFlight = true
      let expandExtraFields
      if (this.showNotActive && !this.showMinQtyItems) {
        expandExtraFields = 'allItemsCount'
      } else if (!this.showNotActive && !this.showMinQtyItems) {
        expandExtraFields = 'activeItemsCount'
      } else if (this.showMinQtyItems) {
        expandExtraFields = 'minQtyWarehouseItems'
      }

      try {
        const { status, data } = await httpClient.post('warehouse/get-dropdown-categories?sort=sorting&expand=' + expandExtraFields, {
          getUncategorizedCount: true,
          showNotActive: this.showNotActive,
          showMinQtyItems: this.showMinQtyItems,
          getMinQtyItemsCount: true,
        })
        if (!this.canUseWarehouse) return
        if (status === 200 && data) {
          this.categories = data.categories ?? []
          this.categoriesList = data.categories?.map(c => {
            // console.log(c)
            return {
              id: c.id,
              name: c.name,
              count: c.activeItemsCount || c.allItemsCount || c.minQtyWarehouseItems || 0
            }
          })

          const uncategorizedCount = data.uncategorized_count ? +data.uncategorized_count : 0

          this.minQtyItemsCount = data.min_qty_items_count

          this.categoriesList.push({id: 0, name: this.$t('Uncategorized'), count: uncategorizedCount })

          if (this.selectedCategoryId || this.selectedCategoryId === 0) {
            this.selectedCategory = this.categoriesList?.find(c => c.id === this.selectedCategoryId)
          } else {
            this.selectedCategory = null
          }

        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.getCategoriesFetchInFlight = false
      }
    },
    updateItems(itemId, getItems = true) {
      this.updatedItemsIds = []
      if (itemId) {
        this.updatedItemId = itemId
      }

      if (getItems) {
        this.getPageData()
      }
      this.$emit('update-items') // Important: this is for update items in parent TAB component
    },
    async getItems(showSpinner = false) {
      if (!this.canUseWarehouse || !this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)

      this.dataTableIsLoading = true
      if (showSpinner) {
        this.isLoading = true
      }

      const ctrl = this.setController?.('warehouseItems')
      try {
        // const { status, data, headers } = await httpClient(`${this.apiLink}?categoryId=${this.selectedCategoryId}&page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            categoryId: this.selectedCategoryId,
            showNotActive: this.showNotActive,
            showMinQtyItems: this.showMinQtyItems,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand
          },
          signal: ctrl?.signal
        })
        if (!this.canUseWarehouse) return
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        if (!this.canUseWarehouse) return
        this.items = []
        this.showError(err)
      } finally {
        this.showNotActiveChanged = false
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.checkAllItemsAreSelected()
        if (this._abortors?.warehouseItems === ctrl) {
          this._abortors.warehouseItems = null
        }
      }
    },
    closeCategoryModal() {
      this.categoryModal = false
      this.$store.commit('toggleFirstLayer', false)
      this.submitted = false
    },
    addNewCategory() {
      this.category = {}
      this.submitted = false
      this.categoryModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
  },
  computed: {
    canModerate() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      return this.itemToDelete?.name ?? null
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px;
}
.radio-button-label {
  padding: 6px;
  margin-left: 0;
  cursor: pointer;
}
.overlay-panel-inner {
  min-width: 110px!important;
}

.title {
  padding-bottom: 10px;
  font-weight: 500;
}

:deep(.billing-selection-checkbox-disabled),
:deep(.billing-selection-checkbox-disabled .p-checkbox-box),
:deep(.billing-selection-checkbox-disabled input) {
  cursor: not-allowed !important;
}
</style>